import ListSubheader from "@mui/material/ListSubheader";
import Typography from "@mui/material/Typography";
import React, { PropsWithChildren, ReactElement } from "react";
import { ListChildComponentProps, VariableSizeList } from "react-window";

/**
 * this module define ListboxComponent for select components with very large lists of options
 * this module utilize react-window library
 * inspired by C8-55547 and https://mui.com/material-ui/react-autocomplete/#virtualization
 */

// additional listbox padding
const LISTBOX_PADDING = 0; // px
// hardcoding height of option as we can't calculate it here
const OPTION_HEIGHT = 26;

const getChildSize = (child: ReactElement, isSlimCard: boolean) => {
	// some items might not have group (example: first item "select none") while other items might have group
	// if given item is group and its child's "key" is not defined (child is not in group), then don't show group element
	const dataSetChild = child.props["data-csi-child"];
	if (dataSetChild?.length && !dataSetChild[0].key) {
		return 0;
	}
	return isSlimCard ? OPTION_HEIGHT*2 : OPTION_HEIGHT;
};

const getHeight = (itemCount: number, itemData: ReactElement[], isSlimCard: boolean) => {
	if (itemCount > 8) {
		return 8 * OPTION_HEIGHT;
	}
	return itemData.map((item) => getChildSize(item, isSlimCard))
		.reduce((a, b) => a + b, 0);
};

function renderRow(props: ListChildComponentProps) {
	const { data, index, style } = props;
	const dataSet = data[index];
	const inlineStyle = {
		...style,
		top: (style.top as number) + LISTBOX_PADDING,
	};

	if (dataSet.props["data-csi-child"]?.length) {
		inlineStyle.paddingLeft = 0;
		return (
			<ListSubheader
				component="div"
				key={dataSet.key}
				style={inlineStyle}
			>
				{dataSet.props.children[0]}
			</ListSubheader>
		);
	}

	return (
		<Typography
			component="div"
			noWrap
			style={inlineStyle}
		>
			{dataSet}
		</Typography>
	);
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
	const outerProps = React.useContext(OuterElementContext);
	return (
		<div
			ref={ref}
			{...props}
			{...outerProps}
		/>
	);
});

function useResetCache(data: any) {
	const ref = React.useRef<VariableSizeList>(null);
	React.useEffect(() => {
		if (ref.current != null) {
			ref.current.resetAfterIndex(0, true);
		}
	}, [data]);
	return ref;
}
export interface ReactWindowListboxComponentProps extends PropsWithChildren {
	/** Determine if the component is a slimCard */
	isSlimCard?: boolean;

	/** ref object to handle keyboard events in large lists */
	largeListRef?: React.ForwardedRef<HTMLDivElement>;
}

// Adapter for react-window
const ReactWindowListboxComponent = (props: ReactWindowListboxComponentProps) => {
	const { children, isSlimCard, largeListRef, ...other } = props;
	const itemData: ReactElement[] = [];
	(children as ReactElement[]).forEach((item: ReactElement & { children?: ReactElement[] }) => {
		itemData.push(item);
		itemData.push(...(item.props["data-csi-child"] || []));
	});
	const itemCount = itemData.length;
	const gridRef = useResetCache(itemCount);

	return (
		<div ref={largeListRef}>
			<OuterElementContext.Provider value={other}>
				<VariableSizeList
					height={getHeight(itemCount, itemData, isSlimCard) + 2 * LISTBOX_PADDING}
					innerElementType="ul"
					itemCount={itemCount}
					itemData={itemData}
					itemSize={(index) => getChildSize(itemData[index], isSlimCard)}
					outerElementType={OuterElementType}
					overscanCount={5}
					ref={gridRef}
					role="listbox"
					width="100%"
				>
					{renderRow}
				</VariableSizeList>
			</OuterElementContext.Provider>
		</div>
	);
};

export default ReactWindowListboxComponent;